<template>
  <main class="section documents-page" aria-labelledby="documents-title">
    <div class="container">
      <h1 id="documents-title" class="title">{{ t('documents.title') }}</h1>
      <p class="subtitle">{{ t('documents.subtitle') }}</p>
      
      <div class="documents-layout">
        <!-- Left Sidebar Navigation -->
        <nav class="sidebar" aria-label="Document categories">
          <ul class="section-list">
            <li v-for="category in categories" :key="category.id" class="section-item">
              <button
                @click="selectCategory(category)"
                :class="['section-button', { active: selectedCategory?.id === category.id }]"
                :aria-pressed="selectedCategory?.id === category.id"
              >
                <div class="section-icon">
                  <span class="category-emoji">{{ category.icon }}</span>
                </div>
                <div class="section-content">
                  <span class="section-title">{{ getCategoryTitle(category) }}</span>
                  <span class="section-desc">{{ getCategoryDesc(category) }}</span>
                </div>
              </button>
            </li>
          </ul>
        </nav>

        <!-- Main Content Area -->
        <main class="content-area">
          <div v-if="!selectedCategory" class="welcome-state">
            <div class="welcome-icon">📁</div>
            <h2>{{ t('documents.selectProduct') }}</h2>
            <p>{{ t('documents.selectDescription') }}</p>
          </div>

          <div v-else-if="loadingFiles" class="loading-state">
            <div class="loading-spinner"></div>
            <p>加载中...</p>
          </div>

          <div v-else class="category-documents">
            <div class="category-header">
              <div class="category-icon-large">{{ selectedCategory.icon }}</div>
              <div class="category-info">
                <h2 class="category-title">{{ getCategoryTitle(selectedCategory) }}</h2>
                <p class="category-desc">{{ getCategoryDesc(selectedCategory) }}</p>
                <div class="file-count">
                  {{ getCurrentFiles().length }} {{ getCurrentFiles().length === 1 ? '个文档' : '个文档' }}
                </div>
              </div>
            </div>

            <div v-if="getCurrentFiles().length === 0" class="empty-state">
              <div class="empty-icon">📄</div>
              <h3>暂无文档</h3>
              <p>该分类下暂无可用文档</p>
            </div>

            <div v-else class="files-grid">
              <div v-for="file in getCurrentFiles()" :key="file.name" class="file-card">
                <div class="file-preview">
                  <div class="file-icon-modern">
                    <span class="file-emoji">{{ file.icon }}</span>
                    <span class="file-extension">{{ file.extension.toUpperCase() }}</span>
                  </div>
                </div>

                <div class="file-info">
                  <h3 class="file-name" :title="file.name">{{ file.name }}</h3>
                  <p class="file-meta">{{ file.size }} • {{ file.type.toUpperCase() }}</p>
                </div>

                <div class="file-actions">
                  <button @click="previewFile(file)" class="action-btn preview-btn" :disabled="!canPreview(file)">
                    {{ t('documents.preview') }}
                  </button>
                  <a :href="file.url" :download="file.name" class="action-btn download-btn">
                    {{ t('documents.download') }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>

    <!-- File Preview Modal -->
    <div v-if="previewModal.show" class="preview-modal" @click="closePreview">
      <div class="preview-content" @click.stop>
        <div class="preview-header">
          <h3>{{ previewModal.file?.name }}</h3>
          <button @click="closePreview" class="close-btn">×</button>
        </div>
        <div class="preview-body">
          <!-- PDF Preview -->
          <div v-if="previewModal.file?.type === 'pdf'" class="pdf-container">
            <iframe
              :src="previewModal.file.url"
              class="pdf-preview"
            ></iframe>
          </div>
          
          <!-- Image Preview -->
          <img 
            v-else-if="isImageType(previewModal.file?.type)" 
            :src="previewModal.file?.url" 
            :alt="previewModal.file?.name"
            class="image-preview"
          />
          
          <!-- Excel Preview (placeholder) -->
          <div v-else-if="previewModal.file?.type === 'excel'" class="excel-preview">
            <div class="excel-placeholder">
              <div class="excel-icon">📊</div>
              <p>Excel file preview</p>
              <p class="excel-note">Download to view full content</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { products } from '@/data/products.js'

const { t, locale } = useI18n()

const selectedCategory = ref(null)
const previewModal = reactive({
  show: false,
  file: null
})
const categoryFiles = ref({})
const loadingFiles = ref(false)

// Map product IDs to icons
const productIcons = {
  'quantum-cloud': '☁️',
  'neural-engine': '🤖',
  'edge-intelligence': '📱',
  'holo-interface': '🖥️',
  'secure-fabric': '🔒'
}

// Generate categories from products data
const categories = computed(() =>
  products.map(product => ({
    id: product.id,
    zh: product.zh.title,
    en: product.en.title,
    icon: productIcons[product.id] || '📄',
    description: {
      zh: product.zh.desc,
      en: product.en.desc
    },
    docPath: product.doc
  }))
)

const getCategoryTitle = (category) => {
  const lang = locale.value.startsWith('zh') ? 'zh' : 'en'
  return category[lang] || category.en
}
const getCategoryDesc = (category) => {
  const lang = locale.value.startsWith('zh') ? 'zh' : 'en'
  return category.description[lang] || category.description.en
}

const selectCategory = async (category) => {
  selectedCategory.value = category
  await loadCategoryFiles(category.id)
}

const getCurrentFiles = () => {
  return selectedCategory.value ? (categoryFiles.value[selectedCategory.value.id] || []) : []
}

const canPreview = (file) => {
  return ['pdf', 'image', 'excel'].includes(file.type)
}

const isImageType = (type) => {
  return type === 'image'
}

const previewFile = (file) => {
  previewModal.file = file
  previewModal.show = true
}

const closePreview = () => {
  previewModal.show = false
  previewModal.file = null
}

// File utilities
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const getFileType = (filename) => {
  const ext = filename.split('.').pop().toLowerCase()
  const typeMap = {
    pdf: 'pdf',
    doc: 'document',
    docx: 'document',
    txt: 'text',
    xlsx: 'excel',
    xls: 'excel',
    png: 'image',
    jpg: 'image',
    jpeg: 'image',
    gif: 'image',
    svg: 'image'
  }
  return typeMap[ext] || 'unknown'
}

const getFileIcon = (type) => {
  const iconMap = {
    pdf: '📄',
    document: '📝',
    text: '📄',
    excel: '📊',
    image: '🖼️',
    unknown: '📋'
  }
  return iconMap[type] || '📋'
}

// Load files for a product
const loadCategoryFiles = async (productId) => {
  loadingFiles.value = true
  try {
    const product = products.find(p => p.id === productId)
    if (!product) {
      categoryFiles.value[productId] = []
      return
    }

    const validFiles = []
    // Use the doc path directly from product data
    const url = product.doc
    const filename = url.split('/').pop()

    try {
      const response = await fetch(url, { method: 'HEAD' })
      if (response.ok) {
        const size = response.headers.get('Content-Length')
        const type = getFileType(filename)
        validFiles.push({
          name: filename,
          url,
          type,
          extension: filename.split('.').pop().toLowerCase(),
          size: size ? formatFileSize(parseInt(size)) : 'Unknown',
          icon: getFileIcon(type)
        })
      }
    } catch (error) {
      console.warn(`File not accessible: ${url}`)
    }

    categoryFiles.value[productId] = validFiles
  } catch (error) {
    console.error('Error loading product files:', error)
    categoryFiles.value[productId] = []
  } finally {
    loadingFiles.value = false
  }
}

onMounted(() => {
  // Load the first product by default
  if (categories.value.length > 0) {
    selectCategory(categories.value[0])
  }
})
</script>

<style scoped>
.section.documents-page {
  min-height: 100vh;
  padding: 2rem 1.5rem;
  background: radial-gradient(1200px 600px at 10% -20%, rgba(96,165,250,0.08), transparent),
              radial-gradient(900px 500px at 110% 120%, rgba(37,99,235,0.10), transparent),
              linear-gradient(180deg, rgba(8,12,24,1) 0%, rgba(10,14,28,1) 100%);
}

.container { max-width: 1200px; margin: 0 auto; }
.title { color: #e6f0ff; font-size: clamp(1.8rem, 4vw, 2.4rem); text-align: center; margin-bottom: 0.5rem; }
.subtitle { color: #9fb0ff; text-align: center; margin-bottom: 2.5rem; }

.documents-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 2rem;
  min-height: 70vh;
}

/* Sidebar */
.sidebar {
  background: linear-gradient(180deg, rgba(18,22,40,0.8), rgba(14,18,34,0.8));
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 1.5rem;
  height: fit-content;
  position: sticky;
  top: 2rem;
}

.section-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.section-item {
  margin-bottom: 0.5rem;
}

.section-button {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  color: #b7c5ff;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}

.section-button:hover {
  background: rgba(255,255,255,0.04);
  border-color: rgba(255,255,255,0.12);
  color: #dbe7ff;
}

.section-button.active {
  background: linear-gradient(135deg, rgba(96,165,250,0.18), rgba(37,99,235,0.18));
  border-color: rgba(96,165,250,0.35);
  color: #e6f0ff;
}

.section-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(96,165,250,0.12), rgba(37,99,235,0.12));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.category-emoji {
  font-size: 1.5rem;
}

.section-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.section-title {
  font-weight: 600;
  font-size: 1rem;
  color: #e6f0ff;
}

.section-desc {
  font-size: 0.85rem;
  color: #9fb0ff;
  opacity: 0.8;
}

/* Main Content */
.content-area {
  background: linear-gradient(180deg, rgba(18,22,40,0.6), rgba(14,18,34,0.6));
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 2rem;
  min-height: 500px;
}

.welcome-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
  color: #9fb0ff;
}

.welcome-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.7;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.category-icon-large {
  font-size: 3rem;
  opacity: 0.9;
}

.category-title {
  color: #e6f0ff;
  font-size: 1.5rem;
  margin: 0 0 0.5rem;
}

.category-desc {
  color: #b7c5ff;
  margin: 0 0 0.5rem;
}

.file-count {
  color: #9fb0ff;
  font-size: 0.9rem;
  opacity: 0.8;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #9fb0ff;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top: 3px solid #60a5fa;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #9fb0ff;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

/* Files Grid */
.files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.file-card {
  background: linear-gradient(180deg, rgba(25,29,47,0.8), rgba(18,22,40,0.8));
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s ease;
}

.file-card:hover {
  border-color: rgba(255,255,255,0.12);
  transform: translateY(-2px);
}

.file-preview {
  margin-bottom: 1rem;
}

.file-icon-modern {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(96,165,250,0.18), rgba(37,99,235,0.18));
  border: 1px solid rgba(255,255,255,0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  position: relative;
}

.file-emoji {
  font-size: 1.5rem;
}

.file-extension {
  font-size: 0.65rem;
  color: #b7c5ff;
  font-weight: 600;
  opacity: 0.8;
}

.file-extension {
  color: white;
}

.file-name {
  color: #e6f0ff;
  font-size: 1rem;
  margin: 0 0 0.25rem;
  word-break: break-word;
  line-height: 1.3;
  max-height: 2.6rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.file-meta {
  color: #9fb0ff;
  font-size: 0.9rem;
  margin: 0 0 1rem;
}

.file-actions {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.preview-btn {
  background: rgba(255,255,255,0.08);
  color: #dbe7ff;
  border: 1px solid rgba(255,255,255,0.12);
}

.preview-btn:hover:not(:disabled) {
  background: rgba(255,255,255,0.12);
}

.preview-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.download-btn {
  background: linear-gradient(135deg, #60a5fa, #2563eb);
  color: white;
  display: inline-block;
}

.download-btn:hover {
  filter: brightness(1.1);
}

/* Preview Modal */
.preview-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
}

.preview-content {
  background: linear-gradient(180deg, rgba(18,22,40,0.95), rgba(14,18,34,0.95));
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 16px;
  width: 95vw;
  height: 95vh;
  max-width: 1400px;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  background: rgba(8,12,24,0.8);
  flex-shrink: 0;
}


.preview-header h3 {
  color: #e6f0ff;
  margin: 0;
  font-size: 1.2rem;
}

.close-btn {
  background: none;
  border: none;
  color: #9fb0ff;
  font-size: 1.5rem;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-btn:hover {
  background: rgba(255,255,255,0.08);
  color: #dbe7ff;
}

.preview-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(8,12,24,0.4);
}

.pdf-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.pdf-preview {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  background: white;
}

.image-preview {
  max-width: 100%;
  max-height: 600px;
  object-fit: contain;
  border-radius: 8px;
}

.excel-preview {
  width: 100%;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.excel-placeholder {
  text-align: center;
  color: #9fb0ff;
}

.excel-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.excel-note {
  font-size: 0.9rem;
  opacity: 0.7;
}

/* Additional PDF styles */
@media (min-width: 769px) {
  .preview-content {
    min-width: 900px;
    min-height: 600px;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .documents-layout {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .sidebar {
    position: static;
  }
  
  .files-grid {
    grid-template-columns: 1fr;
  }
  
  .preview-modal {
    padding: 0.5rem;
  }

  .preview-content {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    border-radius: 8px;
  }

  .preview-header {
    padding: 0.75rem 1rem;
  }

  .section-button {
    padding: 1rem;
  }

  .section-icon {
    width: 40px;
    height: 40px;
  }

  .category-emoji {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 0.9rem;
  }

  .section-desc {
    font-size: 0.8rem;
  }

  .category-header {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .category-icon-large {
    font-size: 2.5rem;
  }
}
</style>